import 'antd/es/input-number/style/index';
import classnames from 'classnames';
import { isNumber } from 'lodash';
import React from 'react';
import { SliderMarks } from 'antd/es/slider';
import InputNumber from '../input-number';
import Slider from '../slider';
import './index.less';

export interface ISliderInput {
    /**
     * 水平还是垂直类型，默认垂直
     *
     * @default "vertical"
     */
    type?: 'horizontal' | 'vertical';

    /**
     * 标题，水平时不展示标题
     */
    title?: React.ReactNode;

    /**
     * 最大值
     *
     * @default 100
     */
    max?: number;

    /**
     * 最小值
     *
     * @default 0
     */
    min?: number;

    /**
     * 数值精度
     *
     * @default 0
     */
    precision?: number;

    /**
     * 禁用
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * 步长
     *
     * - 取值必须大于 0，并且可被 (max - min) 整除
     *
     * @default 1
     */
    step?: number;

    /**
     * 刻度标记
     */
    marks?: SliderMarks;

    /**
     * 气泡框位置
     *
     * @default 'top'
     */
    tooltipPlacement?:
        | 'top'
        | 'left'
        | 'right'
        | 'bottom'
        | 'topLeft'
        | 'topRight'
        | 'bottomLeft'
        | 'bottomRight'
        | 'leftTop'
        | 'leftBottom'
        | 'rightTop'
        | 'rightBottom';

    /**
     * Slider 会把当前值传给 tipFormatter，并在 Tooltip 中显示 tipFormatter 的返回值，若为 null，则隐藏 Tooltip
     */
    tipFormatter?: (value?: number) => React.ReactNode | null;

    /**
     * 值为 true 时，Tooltip 将会始终显示；否则始终不显示，哪怕在拖拽及移入时
     */
    tooltipVisible?: boolean;

    /**
     * 设置初始取值
     */
    defaultValue?: number;

    /**
     * 是否启用键盘快捷行为
     *
     * @default true
     */
    keyboard?: boolean;

    /**
     * 当前值
     */
    value: number;

    /**
     * 输入框内容/滑动条变化时的回调
     */
    onChange?: (value: number) => void;

    /**
     * 输入框内容变化时的回调，或滑动条最后一次值变化的回调
     */
    onAfterChange?: (value: number) => void;

    /**
     * 小数点
     */
    decimalSeparator?: string;

    /**
     * 点击事件自定义回调处理方法
     */
    onClick?: () => void;

    /**
     * 是否显示步长按钮
     *
     * @default false
     */
    showStepBtn?: boolean;
}

const SliderInput: React.FC<ISliderInput> = ({
    type = 'vertical',
    title,
    max = 100,
    min = 0,
    step = 1,
    marks,
    disabled = false,
    tooltipPlacement = 'top',
    tipFormatter,
    tooltipVisible,
    defaultValue,
    value,
    precision = 0,
    decimalSeparator,
    keyboard = true,
    onChange,
    onAfterChange,
    onClick,
    showStepBtn = false,
}) => {
    return (
        <div
            className={classnames('mt-slider-input', {
                'mt-slider-input--horizontal': type === 'horizontal',
                'mt-slider-input--vertical': type === 'vertical',
            })}
        >
            <div className="mt-slider-input__header">
                {type === 'vertical' ? <span>{title}</span> : null}
                <InputNumber
                    max={max}
                    min={min}
                    keyboard={keyboard}
                    disabled={disabled}
                    className="mt-slider-input__input-number"
                    onChange={(val: number | null) => {
                        if (isNumber(val)) {
                            onChange?.(val);
                        }
                    }}
                    decimalSeparator={decimalSeparator}
                    showStepBtn={showStepBtn}
                    precision={precision}
                    defaultValue={defaultValue}
                    value={value}
                    onClick={() => onClick && onClick()}
                    onChangeAfter={(value) => value !== null && onAfterChange?.(value)}
                />
            </div>
            <Slider
                className="mt-slider-input__slider"
                defaultValue={defaultValue}
                marks={marks}
                value={value}
                range={false}
                max={max}
                min={min}
                disabled={disabled}
                step={step}
                tooltip={{ formatter: tipFormatter, placement: tooltipPlacement, open: tooltipVisible }}
                onChange={onChange}
                onAfterChange={onAfterChange}
            />
        </div>
    );
};

export default SliderInput;
